/**
 * Monitor schemas — alerts + time-series metrics + site health roll-ups.
 *
 * Three independent shapes that the Monitor page composes into one view:
 * Alerts (the "what's wrong now" list), MetricPoints (driving the chart),
 * and SiteHealth (per-region progress bars).
 */
import { z } from "zod";

/* ---------- Metric points ---------- */

/** One time-series data point for the monitor event chart. */
export type MetricPoint = {
  /** ISO timestamp for this bin. */
  t: string;
  /** Series name — e.g. "events", "errors", "alerts". */
  series: string;
  /** Numeric value at this bin. */
  value: number;
};

/* ---------- Alerts ---------- */

const AlertSeveritySchema = z.enum(["critical", "warning", "info"]);
export type AlertSeverity = z.infer<typeof AlertSeveritySchema>;

const AlertSchema = z.object({
  id: z.string(),
  severity: AlertSeveritySchema,
  /** Short headline shown in the table cell. */
  title: z.string(),
  /** Where the alert came from — usually a probe or webhook. */
  source: z.string(),
  /** ISO timestamp the alert opened. */
  openedAt: z.string(),
  /** Optional ISO timestamp it was acknowledged. */
  ackedAt: z.string().nullable().optional(),
  vendor: z.enum(["meraki", "aruba", "shasta", "system"]),
  target: z.string(),
  /** Optional structured detail for the expander. */
  detail: z.record(z.unknown()).optional(),
});
export type Alert = z.infer<typeof AlertSchema>;
export const AlertListSchema = z.array(AlertSchema);

/* ---------- Site health roll-up ---------- */

const SiteHealthSchema = z.object({
  name: z.string(),
  hotelName: z.string().nullable().optional(),
  region: z.string(),
  devicesOnline: z.number(),
  devicesTotal: z.number(),
  alertsActive: z.number(),
  /** WAN uplink Mbps last 5m (sum across uplinks). */
  uplinkMbps: z.number().optional(),
});
export type SiteHealth = z.infer<typeof SiteHealthSchema>;
export const SiteHealthListSchema = z.array(SiteHealthSchema);

export const MonitorSitesEnvelopeSchema = z.object({
  sites: SiteHealthListSchema,
  cachedAt: z.string().nullable().optional(),
});
export type MonitorSitesEnvelope = z.infer<typeof MonitorSitesEnvelopeSchema>;

const NetworkEventRowSchema = z.object({
  occurredAt: z.string().nullable(),
  alertTypeId: z.string().nullable().optional(),
  alertType: z.string(),
  deviceSerial: z.string().nullable().optional(),
  emailSentAt: z.string().nullable().optional(),
  pushSentAt: z.string().nullable().optional(),
  smsSentAt: z.string().nullable().optional(),
  webhookSentAt: z.string().nullable().optional(),
  networkId: z.string(),
  networkName: z.string(),
  networkSlug: z.string(),
});
export type NetworkEventRow = z.infer<typeof NetworkEventRowSchema>;
export const NetworkEventRowListSchema = z.array(NetworkEventRowSchema);

const NetworkLicenseRollupSchema = z.object({
  networkId: z.string(),
  networkName: z.string(),
  networkSlug: z.string().nullable().optional().transform((v) => v ?? ""),
  licenseCount: z.number().default(0),
  devicesTotal: z.number().default(0),
  unlicensedCount: z.number().default(0),
  licenseTypes: z.array(z.string()).default([]),
  goodConditionCount: z.number().default(0),
  aboutToExpireCount: z.number().default(0),
  expiredCount: z.number().default(0),
  vendor: z.enum(["meraki", "aruba"]).optional().default("meraki"),
});
export type NetworkLicenseRollup = z.infer<typeof NetworkLicenseRollupSchema>;
export const NetworkLicenseRollupListSchema = z.array(
  NetworkLicenseRollupSchema,
);
